import type { BotIlocaState } from '../types/booking';
import { UNASSIGNED_UNIT_LABEL, unitLabel } from '../config/tenant';

/** Recognize legacy placeholders received from existing booking records. */
export function isUnassignedRoom(room?: string): boolean {
  return unitLabel(room) === UNASSIGNED_UNIT_LABEL;
}

type BookingRoom = {
  source?: string; status?: string; bookingStatus?: string;
  botBooking?: BotIlocaState;
  canCreateInIloca?: boolean;
  room?: { roomNumber?: string; roomName?: string };
  roomNumber?: string;
  roomName?: string;
};

export function bookingRoomLabel(booking: BookingRoom, field: 'number' | 'name' = 'number'): string {
  const room = (field === 'name'
    ? [booking.room?.roomName, booking.roomName, booking.room?.roomNumber, booking.roomNumber]
    : [booking.room?.roomNumber, booking.roomNumber]).find(value => !isUnassignedRoom(value)) || '';
  if (!canMarkEnteredInIloca(booking)) return isUnassignedRoom(room) ? UNASSIGNED_UNIT_LABEL : room;
  const name = booking.room?.roomName || booking.roomName;
  const category = name && !isUnassignedRoom(name) ? name : (isUnassignedRoom(room) ? '' : room);
  return `${UNASSIGNED_UNIT_LABEL}${category ? ` (${category})` : ''}${booking.botBooking?.groupSize ? ` · Gruppe (${booking.botBooking.groupSize} Zimmer)` : ''}`;
}

export type BotBookingAction = BookingRoom & {
  id?: string; checkIn?: string; checkOut?: string; numberOfGuests?: number; roomLabel?: string; totalAmount?: number | null; currencyCode?: string; financial?: { totalAmount?: number | null; currency?: string };
  counts?: { totalGuests?: number }; stay?: { checkIn?: unknown; checkOut?: unknown };
  source?: string;
  status?: string;
  bookingStatus?: string;
  botBooking?: BotIlocaState;
  canCreateInIloca?: boolean;
};

/** Live records may carry non-string source/status values; they never match a bot source or status. */
function upperText(value: unknown): string {
  return typeof value === 'string' ? value.toUpperCase() : '';
}

/** Bot sources (phone/WhatsApp) whose booking has no iLoca counterpart yet. */
export function isBotSource(source: unknown): boolean {
  return ['VOICE', 'WHATSAPP'].includes(upperText(source));
}

export function isBotBookingClosed(booking: BotBookingAction): boolean {
  return booking.botBooking?.enteredInIlocaAt != null;
}

export function canMarkEnteredInIloca(booking: BotBookingAction): boolean {
  return !booking.botBooking?.mergedFromBookingId && isBotSource(booking.source)
    && !isBotBookingClosed(booking)
    && ['CONFIRMED', 'CHANGED', 'MODIFIED', 'PENDING', 'CHECKED_IN']
      .includes(upperText(booking.bookingStatus || booking.status));
}

/** Shared German amount display for booking actions and verification. */
export function formatBookingAmount(amount?: number | null, currency = 'EUR'): string {
  if (amount == null || !Number.isFinite(amount)) return 'Preis fehlt';
  if (amount === 0 && currency === 'EUR') return '0 €';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount).replace('-', '−');
}
